import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import MediaUploadButton from "@/components/MediaUploadButton";
import { ProfilePostTile, ProfilePostModal, MediaFrame } from "@/components/ProfilePost";
import WallPostsPanel from "@/components/WallPostsPanel";
import { toast, Toaster } from "sonner";
import {
  MapPin, Share2, Pencil, Plus, X, Check, Lock, MessageSquare,
  ClipboardList, UserPlus, UserCheck, Images,
} from "lucide-react";

const memberSince = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return `LIMYA member since ${d.toLocaleDateString(undefined, { month: "long", year: "numeric" })}`;
};

/** Owner composer — one visual Profile Post: media + caption + optional category. */
function ComposeModal({ onClose, onPosted }) {
  const [media, setMedia] = useState([]);
  const [caption, setCaption] = useState("");
  const [category, setCategory] = useState("");
  const [cats, setCats] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/categories/master")
      .then(r => setCats((r.data.groups || []).flatMap(g => g.categories.map(c => c.category_name))))
      .catch(() => setCats([]));
  }, []);

  const publish = async () => {
    if (!media.length) { toast.error("Add at least one photo or video"); return; }
    setBusy(true);
    try {
      await api.post("/profile-posts", { caption, category, media });
      toast.success("Posted to your profile");
      onPosted();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not publish");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#222321]/70 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg p-6 my-8" onClick={e => e.stopPropagation()} data-testid="profile-post-composer">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-xl font-bold text-[#222321]" style={{ fontFamily: "Outfit" }}>Share on your profile</h3>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-black/5" data-testid="composer-close"><X size={18}/></button>
        </div>
        <p className="text-sm text-[#222321]/55 mb-5">Photos and videos of your projects — visible on your profile and to neighbors who follow you.</p>

        {media.length > 0 && (
          <div className="grid grid-cols-3 gap-2 mb-4" data-testid="composer-previews">
            {media.map((m, i) => (
              <div key={i} className="relative aspect-square">
                <MediaFrame item={m} className="w-full h-full"/>
                <button onClick={() => setMedia(list => list.filter((_, j) => j !== i))}
                  className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white grid place-items-center"
                  data-testid={`composer-remove-media-${i}`}><X size={12}/></button>
              </div>
            ))}
          </div>
        )}

        <MediaUploadButton onAdd={m => setMedia(list => [...list, m])} disabled={busy}/>

        <textarea rows={3} value={caption} onChange={e => setCaption(e.target.value)}
          placeholder="Add a caption — what did you make, fix or plant?"
          className="w-full mt-4 px-3 py-2.5 text-sm border border-[#D3DAD5] rounded-xl outline-none focus:ring-2 focus:ring-[#2E6B57]"
          data-testid="composer-caption"/>

        <select value={category} onChange={e => setCategory(e.target.value)}
          className="w-full mt-3 px-3 py-2.5 text-sm border border-[#D3DAD5] rounded-xl bg-white outline-none focus:ring-2 focus:ring-[#2E6B57]"
          data-testid="composer-category">
          <option value="">Category (optional)</option>
          {cats.map(c => <option key={c} value={c}>{c}</option>)}
        </select>

        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-[#222321]/60 hover:text-[#222321]" data-testid="composer-cancel">Cancel</button>
          <button onClick={publish} disabled={busy || !media.length}
            className="px-5 py-2.5 text-sm font-semibold rounded-full bg-[#245544] text-white disabled:opacity-40"
            data-testid="composer-publish">{busy ? "Publishing…" : "Publish"}</button>
        </div>
      </div>
    </div>
  );
}

function Restricted({ message }) {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#F5F7F5]">
      <Header/>
      <div className="max-w-md mx-auto px-6 py-24 text-center" data-testid="client-profile-restricted">
        <div className="w-14 h-14 rounded-full bg-[#E9F0EC] grid place-items-center mx-auto mb-5">
          <Lock size={22} className="text-[#245544]"/>
        </div>
        <h1 className="text-2xl font-bold text-[#222321] mb-2" style={{ fontFamily: "Outfit" }}>This profile is private</h1>
        <p className="text-sm text-[#222321]/60 leading-relaxed mb-6">{message}</p>
        <button onClick={() => navigate(-1)} className="px-5 py-2.5 text-sm font-semibold rounded-full border border-[#D3DAD5] bg-white hover:border-[#2E6B57]" data-testid="restricted-back">Go back</button>
      </div>
    </div>
  );
}

/** Pro viewing a Client they actually work(ed) with — identity + context only. */
function LimitedCard({ about, context }) {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#F5F7F5]">
      <Header/>
      <div className="max-w-lg mx-auto px-6 py-16" data-testid="client-limited-card">
        <div className="bg-white rounded-2xl border border-[#D3DAD5] p-7 text-center">
          <span className="w-20 h-20 rounded-full bg-[#E9F0EC] grid place-items-center text-2xl font-bold text-[#245544] mx-auto mb-4 overflow-hidden">
            {about.avatar_url ? <img src={about.avatar_url} alt="" className="w-full h-full object-cover"/> : about.name?.[0]}
          </span>
          <h1 className="text-2xl font-bold text-[#222321]" style={{ fontFamily: "Outfit" }} data-testid="limited-name">{about.name}</h1>
          {about.city && <p className="text-sm text-[#222321]/60 mt-1 flex items-center justify-center gap-1"><MapPin size={13}/> {about.city}</p>}
          <p className="text-xs text-[#222321]/45 mt-1">{memberSince(about.member_since)}</p>
          {context?.request_title && (
            <p className="text-sm text-[#222321]/70 mt-4 bg-[#F5F7F5] rounded-xl px-3 py-2">Request: {context.request_title}</p>
          )}
          <div className="flex flex-wrap justify-center gap-2 mt-6">
            {context?.request_id && (
              <button onClick={() => navigate(`/requests/${context.request_id}`)} className="px-4 py-2.5 text-sm font-semibold rounded-full bg-[#245544] text-white flex items-center gap-1.5" data-testid="limited-view-request">
                <ClipboardList size={15}/> View Request
              </button>
            )}
            <button onClick={() => navigate(`/messages?user=${about.id}`)} className="px-4 py-2.5 text-sm font-semibold rounded-full border border-[#D3DAD5] bg-white hover:border-[#2E6B57] flex items-center gap-1.5" data-testid="limited-message">
              <MessageSquare size={15}/> Message
            </button>
          </div>
          <p className="text-[11px] text-[#222321]/40 mt-6 leading-relaxed">Neighbor profiles on LIMYA are a client-to-client space. Communication stays in LIMYA.</p>
        </div>
      </div>
    </div>
  );
}

export default function ClientProfile() {
  const { id } = useParams();
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [openPost, setOpenPost] = useState(null);
  const [composing, setComposing] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ name: "", city: "", avatar_url: "" });
  const [saving, setSaving] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);

  const load = () => api.get(`/clients/${id}/public`)
    .then(r => { setData(r.data); setError(""); })
    .catch(e => setError(e.response?.data?.detail || "Profile not found"));

  useEffect(() => { setData(null); load(); /* eslint-disable-next-line */ }, [id]);

  if (error) {
    return <Restricted message={error}/>;
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-[#F5F7F5]">
        <Header/>
        <div className="max-w-5xl mx-auto px-6 py-24 text-center text-[#222321]/40 text-sm">Loading profile…</div>
      </div>
    );
  }
  if (data.limited) return <LimitedCard about={data.about} context={data.context}/>;

  const { about, is_self, posts = [], is_following } = data;

  const toggleFollow = async () => {
    setFollowBusy(true);
    try {
      await api.post(`/users/${is_following ? "unfollow" : "follow"}/${about.id}`);
      toast.success(is_following ? "Unfollowed" : `Following ${about.name.split(" ")[0]}`);
      await refresh?.();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
    finally { setFollowBusy(false); }
  };

  const share = async () => {
    const url = `${window.location.origin}/client/${about.id}`;
    try {
      if (navigator.share) await navigator.share({ title: about.name, url });
      else { await navigator.clipboard.writeText(url); toast.success("Profile link copied"); }
    } catch { /* dismissed */ }
  };

  const startEdit = () => {
    setDraft({ name: about.name || "", city: about.city || "", avatar_url: about.avatar_url || "" });
    setEditing(true);
  };
  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.patch("/users/me", draft);
      await refresh?.();
      setEditing(false);
      load();
      toast.success("Profile updated");
    } catch { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-[#F5F7F5]">
      <Header/>
      <Toaster position="top-center"/>

      <div className="max-w-5xl mx-auto px-5 sm:px-6 pt-10 sm:pt-14 pb-20">
        {/* Header — photo, name, location, member since, actions */}
        <div className="flex flex-col sm:flex-row sm:items-end gap-5 sm:gap-7 pb-8 sm:pb-10" data-testid="client-profile-header">
          <span className="w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-[#E9F0EC] grid place-items-center text-3xl font-bold text-[#245544] overflow-hidden shrink-0 ring-4 ring-white shadow-[0_8px_24px_-14px_rgba(34,35,33,0.5)]">
            {(editing ? draft.avatar_url : about.avatar_url)
              ? <img src={editing ? draft.avatar_url : about.avatar_url} alt="" className="w-full h-full object-cover"/>
              : about.name?.[0]}
          </span>

          <div className="flex-1 min-w-0">
            {editing ? (
              <div className="space-y-2.5 max-w-sm" data-testid="client-profile-header-editor">
                <input value={draft.name} onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
                  className="w-full text-2xl font-bold px-3 py-1.5 border border-[#D3DAD5] rounded-xl outline-none focus:ring-2 focus:ring-[#2E6B57]"
                  style={{ fontFamily: "Outfit" }} data-testid="client-profile-name-input"/>
                <input value={draft.city} onChange={e => setDraft(d => ({ ...d, city: e.target.value }))}
                  placeholder="City, State" className="w-full text-sm px-3 py-1.5 border border-[#D3DAD5] rounded-xl outline-none focus:ring-2 focus:ring-[#2E6B57]"
                  data-testid="client-profile-city-input"/>
                <div className="flex gap-2">
                  <button onClick={saveEdit} disabled={saving} className="px-4 py-2 text-xs font-semibold rounded-full bg-[#245544] text-white flex items-center gap-1 disabled:opacity-50" data-testid="client-profile-header-save"><Check size={13}/> {saving ? "Saving…" : "Save"}</button>
                  <button onClick={() => setEditing(false)} className="px-3 py-2 text-xs font-semibold text-[#222321]/55" data-testid="client-profile-header-cancel">Cancel</button>
                </div>
              </div>
            ) : (
              <>
                <h1 className="text-3xl sm:text-4xl font-bold text-[#222321] leading-tight" style={{ fontFamily: "Outfit" }} data-testid="client-profile-name">{about.name}</h1>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-sm text-[#222321]/60">
                  {about.city && <span className="flex items-center gap-1" data-testid="client-profile-city"><MapPin size={14}/> {about.city}</span>}
                  <span className="text-xs text-[#222321]/45" data-testid="client-profile-member-since">{memberSince(about.member_since)}</span>
                </div>
              </>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2 shrink-0">
            {is_self ? (
              <>
                <button onClick={() => setComposing(true)} className="px-4 py-2.5 text-sm font-semibold rounded-full bg-[#245544] text-white flex items-center gap-1.5 hover:bg-[#2E6B57] transition-colors" data-testid="client-profile-new-post">
                  <Plus size={15}/> New post
                </button>
                {!editing && (
                  <button onClick={startEdit} className="px-4 py-2.5 text-sm font-semibold rounded-full border border-[#D3DAD5] bg-white hover:border-[#2E6B57] flex items-center gap-1.5 transition-colors" data-testid="client-profile-edit">
                    <Pencil size={14}/> Edit Profile
                  </button>
                )}
              </>
            ) : (
              <button onClick={toggleFollow} disabled={followBusy}
                className={`px-5 py-2.5 text-sm font-semibold rounded-full flex items-center gap-1.5 transition-colors disabled:opacity-50 ${is_following ? "border border-[#D3DAD5] bg-white text-[#222321] hover:border-[#2E6B57]" : "bg-[#245544] text-white hover:bg-[#2E6B57]"}`}
                data-testid="client-profile-follow">
                {is_following ? <><UserCheck size={15}/> Following</> : <><UserPlus size={15}/> Follow</>}
              </button>
            )}
            <button onClick={share} className="w-10 h-10 rounded-full border border-[#D3DAD5] bg-white hover:border-[#2E6B57] grid place-items-center transition-colors" title="Share profile" data-testid="client-profile-share">
              <Share2 size={15}/>
            </button>
          </div>
        </div>

        <div className="h-px bg-[#D3DAD5] mb-8 sm:mb-10"/>

        {/* One clean visual grid — media first */}
        {posts.length === 0 ? (
          <div className="text-center py-16 sm:py-24" data-testid="client-profile-empty">
            <div className="w-14 h-14 rounded-2xl bg-white border border-[#D3DAD5] grid place-items-center mx-auto mb-5 rotate-[-3deg]">
              <Images size={22} className="text-[#2E6B57]"/>
            </div>
            <h2 className="text-lg font-bold text-[#222321] mb-1" style={{ fontFamily: "Outfit" }}>
              {is_self ? "Nothing published yet" : `${about.name.split(" ")[0]} hasn't published anything yet`}
            </h2>
            <p className="text-sm text-[#222321]/55 max-w-sm mx-auto">
              {is_self
                ? "Share a photo or a short video of a project — it lands here and in the feeds of neighbors who follow you."
                : "Follow along and their projects will show up in your feed."}
            </p>
            {is_self && (
              <button onClick={() => setComposing(true)} className="mt-6 px-5 py-2.5 text-sm font-semibold rounded-full bg-[#245544] text-white inline-flex items-center gap-1.5" data-testid="client-profile-empty-post">
                <Plus size={15}/> Create your first post
              </button>
            )}
          </div>
        ) : (
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-5" data-testid="client-profile-grid">
            {posts.map(p => <ProfilePostTile key={p.id} post={p} onOpen={() => setOpenPost(p.id)}/>)}
          </div>
        )}

        {/* Owner-only: manage Requests & Recommendations published to the Wall.
            Clients have no dashboard (they're redirected here), so this is the
            only place these tools can live. */}
        {is_self && (
          <div className="mt-14" data-testid="client-profile-wall-posts">
            <WallPostsPanel/>
          </div>
        )}
      </div>

      {composing && <ComposeModal onClose={() => setComposing(false)} onPosted={() => { setComposing(false); load(); }}/>}
      {openPost && <ProfilePostModal postId={openPost} onClose={() => setOpenPost(null)} onChanged={load}/>}
    </div>
  );
}
